import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/apiClient";
import { DownloadPdfButton } from "@/components/DownloadPdfButton";
import { X, Activity, Brain, Cpu, Sparkles, TrendingDown, TrendingUp, ShieldAlert, Loader2, Radio, ChevronRight, Plus, Gauge, CalendarClock } from "lucide-react";

const statusColor = { READY: "#ccff00", MANAGED: "#ffb800", DOUBT: "#ff7300", OUT: "#ff3b30", UNTESTED: "#5b6cff" };
const rColor = (v) => (v >= 75 ? "#ccff00" : v >= 60 ? "#ffb800" : v >= 40 ? "#ff7300" : "#ff3b30");
const mColor = (v) => (v >= 80 ? "#ccff00" : v >= 68 ? "#33d17a" : v >= 52 ? "#00e5ff" : v >= 38 ? "#ffb800" : "#ff3b30");
const riskColor = (v) => (v >= 55 ? "#ff3b30" : v >= 30 ? "#ff7300" : v >= 15 ? "#ffb800" : "#33d17a");
const acwrColor = (r) => (r < 0.8 ? "#00e5ff" : r <= 1.3 ? "#33d17a" : r <= 1.5 ? "#ffb800" : "#ff3b30");

const Section = ({ icon: Icon, title, tag, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
    <div className="mb-4 flex items-center gap-2">
      <Icon size={16} className="txt-cyan" />
      <h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">{title}</h3>
      {tag && <span className="ml-auto font-mono text-[10px] text-white/35">{tag}</span>}
    </div>
    {children}
  </div>
);

const FactorBar = ({ label, value, unit = "%", color }) => (
  <div>
    <div className="mb-1 flex justify-between text-[10px] text-white/45"><span>{label}</span><span style={{ color: color || "#fff" }}>{value}{unit}</span></div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/8">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, value)}%`, background: color || "#00e5ff", boxShadow: `0 0 8px ${color || "#00e5ff"}` }} />
    </div>
  </div>
);

// unnormalised Beta shape x^(a-1)(1-x)^(b-1), scaled to its own peak — enough for the curve
function BetaCurve({ alpha, beta, mean, band, width = 320, height = 96 }) {
  const path = useMemo(() => {
    const n = 60, xs = [], ys = [];
    let max = 1e-9;
    for (let i = 0; i <= n; i++) {
      const x = i / n;
      const y = Math.pow(Math.max(x, 1e-6), alpha - 1) * Math.pow(Math.max(1 - x, 1e-6), beta - 1);
      xs.push(x); ys.push(y); if (y > max) max = y;
    }
    return xs.map((x, i) => `${i === 0 ? "M" : "L"}${(x * width).toFixed(1)},${(height - (ys[i] / max) * (height - 6) - 2).toFixed(1)}`).join(" ");
  }, [alpha, beta, width, height]);
  const meanX = mean * width;
  const lo = (band?.low ?? 0) / 100 * width;
  const hi = (band?.high ?? 100) / 100 * width;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" data-testid="beta-curve">
      <rect x={lo} y={0} width={Math.max(1, hi - lo)} height={height} fill="#00e5ff" opacity="0.10" />
      <path d={path} fill="none" stroke="#00e5ff" strokeWidth="2" style={{ filter: "drop-shadow(0 0 6px rgba(0,229,255,0.6))" }} />
      <line x1={meanX} y1={0} x2={meanX} y2={height} stroke={mColor(mean * 100)} strokeWidth="1.5" strokeDasharray="3 3" />
    </svg>
  );
}

function Forecast({ data }) {
  if (!data?.length) return <p className="text-[11px] text-white/40">Unavailable while flagged out.</p>;
  const w = 300, h = 90, max = 100;
  const step = w / (data.length - 1 || 1);
  const pts = data.map((d, i) => `${(i * step).toFixed(1)},${(h - (d.readiness / max) * (h - 10) - 4).toFixed(1)}`);
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="readiness-forecast">
        <polyline points={pts.join(" ")} fill="none" stroke="#ff7300" strokeWidth="2" style={{ filter: "drop-shadow(0 0 6px rgba(255,115,0,0.5))" }} />
        {data.map((d, i) => <circle key={i} cx={i * step} cy={h - (d.readiness / max) * (h - 10) - 4} r="3" fill={rColor(d.readiness)} />)}
      </svg>
      <div className="mt-1 flex justify-between font-mono text-[9px] text-white/40">
        {data.map((d) => <span key={d.match}>G{d.match}·{d.readiness}%</span>)}
      </div>
    </div>
  );
}

function KnowledgeBlock({ text }) {
  const records = useMemo(() => {
    if (!text) return [];
    const idx = text.indexOf("---");
    const body = idx >= 0 ? text.slice(idx) : text;
    return body.split(/\n(?=---)/).map((b) => b.trim()).filter((b) => b.startsWith("---"));
  }, [text]);
  if (!records.length) return <p className="text-[11px] text-white/40">No directly relevant fleet records surfaced for this profile.</p>;
  return (
    <div className="space-y-3">
      {records.map((r, i) => {
        const head = (r.match(/---\s*(.*?)\s*---/) || [, ""])[1];
        const compartment = (head.match(/compartment:\s*([^·]+)/) || [, ""])[1].trim();
        const title = (head.match(/FILED RECORD:\s*([^·]+)/) || [, ""])[1].trim();
        const bodyTxt = r.replace(/---.*?---/s, "").trim();
        return (
          <div key={i} className="rounded-xl border border-cyan-400/15 bg-cyan-400/[0.03] p-3" data-testid={`fleet-record-${i}`}>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-cyan-400/10 px-2 py-0.5 font-tac text-[8px] tracking-widest txt-cyan"><Radio size={9} /> {compartment || "AVA"}</span>
              {title && <span className="text-[10px] font-semibold text-white/70">{title}</span>}
            </div>
            <p className="text-[11px] leading-relaxed text-white/55">{bodyTxt.slice(0, 520)}{bodyTxt.length > 520 ? "…" : ""}</p>
          </div>
        );
      })}
    </div>
  );
}

export function PlayerDossier({ teamId, player: initial, onClose }) {
  const [d, setD] = useState(null);
  const [deep, setDeep] = useState(null);
  const [deepLoading, setDeepLoading] = useState(true);
  const [openSig, setOpenSig] = useState(null);
  const [logMin, setLogMin] = useState(20);
  const [logging, setLogging] = useState(false);
  const [nudge, setNudge] = useState(null);
  const pdfRef = useRef(null);
  // Hands-free: the voice agent (close_card) or the Escape key closes this dossier.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("octon:close-overlays", onClose);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("octon:close-overlays", onClose); window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const loadDossier = () =>
    api.get(`/players/dossier?team_id=${teamId}&player_id=${initial.id}`).then((r) => setD(r.data)).catch(() => {});

  useEffect(() => {
    let on = true;
    setD(null); setDeep(null); setDeepLoading(true); setNudge(null);
    api.get(`/players/dossier?team_id=${teamId}&player_id=${initial.id}`).then((r) => on && setD(r.data)).catch(() => {});
    api.get(`/players/deep-read?team_id=${teamId}&player_id=${initial.id}`).then((r) => on && setDeep(r.data)).catch(() => {}).finally(() => on && setDeepLoading(false));
    return () => { on = false; };
  }, [teamId, initial.id]);

  // Ramp Nudge: which upcoming fixture to hand him his next appearance (only for match-unproven players)
  useEffect(() => {
    if (d?.sharpness_ramp && !d.sharpness_ramp.match_ready) {
      let on = true;
      api.get(`/players/ramp-nudge?team_id=${teamId}&player_id=${initial.id}`).then((r) => on && setNudge(r.data.nudge)).catch(() => {});
      return () => { on = false; };
    }
    setNudge(null);
  }, [d?.sharpness_ramp?.logged_minutes, d?.sharpness_ramp?.match_ready, teamId, initial.id]);

  const logMinutes = () => {
    setLogging(true);
    api.post("/players/sharpness-log", { team_id: teamId, player_id: initial.id, minutes: logMin })
      .then((r) => setD((prev) => ({ ...prev, sharpness_ramp: r.data.sharpness_ramp })))
      .catch(() => {}).finally(() => setLogging(false));
  };
  const resetRamp = () => {
    setLogging(true);
    api.post("/players/sharpness-log/reset", { team_id: teamId, player_id: initial.id, minutes: 0 })
      .then((r) => setD((prev) => ({ ...prev, sharpness_ramp: r.data.sharpness_ramp })))
      .catch(() => {}).finally(() => setLogging(false));
  };

  const p = d?.player || initial;
  const m = p.mindset || {};
  const f = p.factors || {};
  const by = m.bayes || {};
  const contrib = d?.contributions;
  const injury = d?.injury_risk;
  const rec = d?.recommendation;
  const ramp = d?.sharpness_ramp;
  const acwr = d?.acwr;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex justify-center overflow-y-auto bg-black/80 p-3 backdrop-blur-md sm:p-6" onClick={onClose} data-testid="player-dossier"
        data-octon-overlay="player dossier readiness card fitness deep read" data-octon-label={`Player dossier: ${initial?.name || ""}`}
        data-octon-state={d ? "ready" : "loading"}>
        <motion.div initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          className="relative my-auto h-fit w-full max-w-3xl rounded-3xl border border-white/10 bg-[#080808] shadow-[0_0_60px_rgba(0,229,255,0.15)]" onClick={(e) => e.stopPropagation()} ref={pdfRef}>

          {/* header */}
          <div className="sticky top-0 z-10 flex items-center gap-4 rounded-t-3xl border-b border-white/10 bg-[#080808]/95 p-5 backdrop-blur">
            <div className="relative shrink-0">
              <img src={p.photo} alt={p.name} className="h-16 w-16 rounded-2xl object-cover" style={{ boxShadow: `0 0 0 2px ${statusColor[p.status]}66` }} onError={(e) => { e.target.style.opacity = 0.25; }} />
              <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-[#080808]" style={{ background: statusColor[p.status], boxShadow: `0 0 8px ${statusColor[p.status]}` }} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="truncate font-head text-2xl font-black tracking-tight">{p.name}</h2>
              <p className="text-[11px] text-white/45">{p.position_detail}{p.number ? ` · #${p.number}` : ""}{p.age ? ` · ${p.age}y` : ""}</p>
              <span className="mt-1 inline-block rounded-full px-2 py-0.5 font-tac text-[8px] tracking-widest" style={{ background: `${statusColor[p.status]}1f`, color: statusColor[p.status] }}>{p.status}</span>
            </div>
            <div className="text-right">
              <p className="font-head text-4xl font-black" style={{ color: rColor(p.readiness) }}>{p.readiness}%</p>
              <p className="font-tac text-[8px] tracking-widest text-white/35">READINESS</p>
            </div>
            <DownloadPdfButton targetRef={pdfRef} title={`${p.name} — Player Dossier`} subtitle="Player Dossier" meta={[p.position_detail || p.position, p.age ? `${p.age}y` : ""]} filename={`octon-dossier-${(p.name || "player").replace(/\s+/g, "-").toLowerCase()}.pdf`} label="PDF" testid="dossier-pdf-btn" />
            <button onClick={onClose} className="ml-1 rounded-xl p-2 text-white/50 hover:bg-white/10 hover:text-white" data-testid="dossier-close"><X size={18} /></button>
          </div>

          <div className="space-y-4 p-5">
            {/* recommendation */}
            {rec && (
              <div className="flex items-center gap-3 rounded-2xl border px-4 py-3" style={{ borderColor: `${rColor(p.readiness)}44`, background: `${rColor(p.readiness)}0d` }} data-testid="dossier-recommendation">
                <Sparkles size={16} style={{ color: rColor(p.readiness) }} />
                <div><p className="font-head text-lg font-bold" style={{ color: rColor(p.readiness) }}>{rec.call}</p><p className="text-[11px] text-white/55">{rec.note}</p></div>
              </div>
            )}

            {/* readiness science */}
            <Section icon={Activity} title="READINESS · SPORTS SCIENCE" tag="load model">
              <div className="grid gap-3 sm:grid-cols-2">
                <FactorBar label="Match load" value={f.load ?? 0} color="#ff7300" />
                <FactorBar label="Freshness" value={f.freshness ?? 0} color="#00e5ff" />
                <FactorBar label="Age recovery" value={f.age_score ?? 0} color="#33d17a" />
                <FactorBar label="Readiness" value={p.readiness} color={rColor(p.readiness)} />
              </div>
              <p className="mt-3 rounded-lg bg-black/40 p-2 font-mono text-[10px] text-white/50" data-testid="readiness-formula">
                readiness = 0.6·freshness({f.freshness}%) + 0.4·age-recovery({f.age_score}%) = <span style={{ color: rColor(p.readiness) }}>{p.readiness}%</span>
                <br />load = minutes({f.minutes}) / 2700 → {f.load}% · from {f.apps} apps
              </p>
              {d?.readiness_breakdown && (
                <div className="mt-3 overflow-hidden rounded-lg border border-white/8" data-testid="readiness-breakdown">
                  <div className="grid grid-cols-12 gap-1 bg-white/[0.04] px-3 py-1.5 font-tac text-[8px] tracking-widest text-white/40">
                    <span className="col-span-3">FACTOR</span><span className="col-span-2">INPUT</span><span className="col-span-2 text-right">×WEIGHT</span><span className="col-span-5">THE SCIENCE</span>
                  </div>
                  {d.readiness_breakdown.components.map((c) => (
                    <div key={c.name} className="grid grid-cols-12 items-start gap-1 border-t border-white/5 px-3 py-2 text-[11px]">
                      <span className="col-span-3 font-semibold text-white/80">{c.name}</span>
                      <span className="col-span-2 font-mono text-white/50">{c.value}%</span>
                      <span className="col-span-2 text-right font-mono txt-cyan">{c.value}×{c.weight}={c.contribution}</span>
                      <span className="col-span-5 leading-snug text-white/50">{c.science}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* sharpness ramp + tracker — only for match-unproven (UNTESTED) players */}
            {ramp && (
              <Section icon={TrendingUp} title="SHARPNESS RAMP · MINUTES TO MATCH-READY" tag={ramp.match_ready ? "MATCH-READY ✓" : `≈${ramp.matches_to_match_ready} appearances`}>
                <div className="mb-2 flex items-baseline gap-3" data-testid="sharpness-ramp">
                  <p className="font-head text-3xl font-black text-[#5b6cff]">{ramp.target_minutes}′</p>
                  <span className="text-[12px] text-white/60">{ramp.headline}</span>
                </div>
                {/* live progress from real logged minutes */}
                <div className="mb-3" data-testid="ramp-progress">
                  <div className="mb-1 flex justify-between text-[10px] text-white/45">
                    <span>Logged so far: <span className="font-mono text-white/80">{ramp.logged_minutes}′</span> / {ramp.target_minutes}′</span>
                    <span>Current sharpness: <span className="font-mono" style={{ color: mColor(ramp.current_readiness) }}>{ramp.current_readiness}%</span></span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-white/8">
                    <motion.div className="h-full rounded-full" style={{ background: "#5b6cff", boxShadow: "0 0 10px #5b6cff" }}
                      initial={{ width: 0 }} animate={{ width: `${ramp.progress_pct}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2" data-testid="sharpness-steps">
                  {ramp.steps.map((s) => (
                    <div key={s.stage} className={`rounded-xl border p-2.5 text-center transition ${s.done ? "border-[#5b6cff]/60 bg-[#5b6cff]/10" : "border-white/8 bg-white/[0.02]"}`}>
                      <p className="font-tac text-[8px] tracking-widest text-white/35">{s.window}{s.done ? " ✓" : ""}</p>
                      <p className="mt-1 font-head text-xl font-black" style={{ color: s.done ? "#5b6cff" : mColor(s.projected_readiness) }}>{s.projected_readiness}%</p>
                      <div className="mx-auto mt-1 h-1 w-full overflow-hidden rounded-full bg-white/8">
                        <div className="h-full rounded-full" style={{ width: `${s.done ? 100 : s.projected_readiness}%`, background: s.done ? "#5b6cff" : mColor(s.projected_readiness) }} />
                      </div>
                      <p className="mt-1.5 text-[9px] leading-tight text-white/45">{s.minutes}′ · {s.role}</p>
                    </div>
                  ))}
                </div>
                {/* log real bench minutes */}
                <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-white/8 bg-black/30 p-3" data-testid="ramp-tracker">
                  <span className="font-tac text-[9px] tracking-widest text-white/40">LOG REAL MINUTES</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setLogMin((v) => Math.max(1, v - 5))} className="rounded-md border border-white/10 px-2 py-1 text-white/60 hover:text-white" data-testid="ramp-min-minus">−</button>
                    <span className="w-10 text-center font-head text-lg font-black" data-testid="ramp-min-value">{logMin}′</span>
                    <button onClick={() => setLogMin((v) => Math.min(120, v + 5))} className="rounded-md border border-white/10 px-2 py-1 text-white/60 hover:text-white" data-testid="ramp-min-plus">+</button>
                  </div>
                  <button onClick={logMinutes} disabled={logging} className="inline-flex items-center gap-1.5 rounded-full bg-[#5b6cff] px-4 py-1.5 text-[12px] font-bold text-white hover:brightness-110 disabled:opacity-50" data-testid="ramp-log-btn">
                    {logging ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} Log appearance
                  </button>
                  {ramp.logged_minutes > 0 && <button onClick={resetRamp} disabled={logging} className="ml-auto text-[11px] text-white/40 hover:text-white/70" data-testid="ramp-reset-btn">reset</button>}
                </div>
                {ramp.log?.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="ramp-log-entries">
                    {ramp.log.slice(0, 5).map((e, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px] text-white/55">
                        <span><span className="font-mono text-[#5b6cff]">+{e.minutes}′</span>{e.note ? ` · ${e.note}` : ""}</span>
                        <span className="font-mono text-white/30">{new Date(e.ts).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Ramp Nudge — the right fixture for his next appearance */}
                {nudge && (
                  <div className="mt-3 rounded-xl border border-[#5b6cff]/30 bg-[#5b6cff]/[0.06] p-3" data-testid="ramp-nudge">
                    <div className="mb-1 flex items-center gap-2">
                      <CalendarClock size={13} className="text-[#a6b0ff]" />
                      <span className="font-tac text-[9px] tracking-widest text-[#a6b0ff]">OCTON'S NEXT-APPEARANCE NUDGE</span>
                    </div>
                    <div className="flex items-center gap-2 text-[13px] font-semibold text-white/90">
                      {nudge.opponent_logo && <img src={nudge.opponent_logo} alt="" className="h-5 w-5 object-contain" />}
                      <span>{nudge.home ? "vs" : "@"} {nudge.opponent}</span>
                      <span className="rounded-md bg-white/10 px-1.5 py-0.5 font-mono text-[10px] text-white/60">{nudge.home ? "HOME" : "AWAY"}</span>
                      {nudge.date && <span className="font-mono text-[10px] text-white/40">{new Date(nudge.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
                    </div>
                    <p className="mt-1.5 text-[12px] leading-snug text-white/65">{nudge.reason}</p>
                  </div>
                )}
                <p className="mt-3 text-[10px] leading-snug text-white/45">{ramp.science}</p>
              </Section>
            )}

            {/* ACWR — acute:chronic workload (live club feed or modelled) */}
            {acwr && (
              <Section icon={Gauge} title="ACWR · ACUTE:CHRONIC WORKLOAD" tag={acwr.source === "live" ? "LIVE FEED" : "MODELLED"}>
                <div className="mb-3 flex flex-wrap items-end gap-4" data-testid="acwr-chart">
                  <div>
                    <p className="font-head text-5xl font-black" style={{ color: acwrColor(acwr.ratio) }}>{acwr.ratio}</p>
                    <p className="font-tac text-[9px] tracking-widest" style={{ color: acwrColor(acwr.ratio) }}>{acwr.status.toUpperCase()}</p>
                  </div>
                  <div className="flex gap-4 font-mono text-[11px] text-white/60">
                    <div><span className="block text-white/30">ACUTE 7d</span>{acwr.acute}</div>
                    <div><span className="block text-white/30">CHRONIC 28d</span>{acwr.chronic}</div>
                    <div><span className="block text-white/30">CORRIDOR</span>{acwr.corridor[0]}–{acwr.corridor[1]}</div>
                  </div>
                </div>
                {/* 28-day load bars, last 7 (acute) highlighted */}
                <div className="flex h-16 items-end gap-[3px]" data-testid="acwr-bars">
                  {acwr.days.map((day, i) => {
                    const max = Math.max(...acwr.days.map((x) => x.load), 1);
                    const acute = i >= acwr.days.length - 7;
                    return <div key={i} title={`${day.date}: ${day.load}`} className="flex-1 rounded-t" style={{ height: `${Math.max(4, (day.load / max) * 100)}%`, background: acute ? acwrColor(acwr.ratio) : "rgba(255,255,255,0.14)" }} />;
                  })}
                </div>
                <div className="mt-1 flex justify-between font-tac text-[8px] tracking-widest text-white/30"><span>28 DAYS AGO</span><span style={{ color: acwrColor(acwr.ratio) }}>◼ LAST 7 (ACUTE)</span><span>TODAY</span></div>
                <p className="mt-3 text-[10px] leading-snug text-white/45">{acwr.note} {acwr.science}</p>
              </Section>
            )}

            {/* predictive */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Section icon={TrendingDown} title="READINESS FORECAST" tag="next 4 starts">
                <Forecast data={d?.forecast} />
                <p className="mt-2 text-[10px] leading-snug text-white/45">Projected decline if he starts every fixture — fatigue accrues faster than his age-recovery replenishes it.</p>
              </Section>
              <Section icon={ShieldAlert} title="INJURY RISK" tag="predictive">
                {injury ? (
                  <>
                    <div className="flex items-end gap-2"><p className="font-head text-5xl font-black" style={{ color: riskColor(injury.pct) }}>{injury.pct}%</p><span className="mb-2 font-tac text-[10px] tracking-widest" style={{ color: riskColor(injury.pct) }}>{injury.level.toUpperCase()}</span></div>
                    <ul className="mt-2 space-y-1">{injury.drivers.map((x, i) => <li key={i} className="text-[10px] text-white/50">• {x}</li>)}</ul>
                  </>
                ) : <Loader2 className="animate-spin text-white/30" size={16} />}
              </Section>
            </div>

            {/* injury risk model breakdown */}
            {injury?.components && (
              <Section icon={ShieldAlert} title="INJURY RISK · MODEL BREAKDOWN" tag="facts × science">
                <div className="mb-3 flex items-baseline gap-3" data-testid="injury-breakdown">
                  <p className="font-head text-4xl font-black" style={{ color: riskColor(injury.pct) }}>{injury.pct}%</p>
                  <span className="font-tac text-[11px] tracking-widest" style={{ color: riskColor(injury.pct) }}>{injury.level.toUpperCase()} RISK</span>
                  {injury.clamped && <span className="text-[10px] text-white/35">(raw {injury.raw}% → capped to a sane 2–96% band)</span>}
                </div>
                {/* stacked contribution bar */}
                <div className="mb-1 flex h-3 w-full overflow-hidden rounded-full bg-white/8" data-testid="injury-stack">
                  {injury.components.filter((c) => c.points > 0).map((c, i) => {
                    const share = injury.raw > 0 ? (c.points / injury.raw) * 100 : 0;
                    const cols = ["#5b6cff", "#ff7300", "#ff3b30", "#ffb800", "#e11d48"];
                    return <div key={c.name} title={`${c.name}: +${c.points}`} style={{ width: `${share}%`, background: cols[i % cols.length] }} />;
                  })}
                </div>
                <p className="mb-3 rounded-lg bg-black/40 p-2 font-mono text-[10px] leading-relaxed text-white/55">{injury.formula}</p>
                {/* per-factor table with science */}
                <div className="overflow-hidden rounded-lg border border-white/8">
                  <div className="grid grid-cols-12 gap-1 bg-white/[0.04] px-3 py-1.5 font-tac text-[8px] tracking-widest text-white/40">
                    <span className="col-span-3">FACTOR</span><span className="col-span-2">INPUT</span><span className="col-span-1 text-right">+PTS</span><span className="col-span-6">THE SCIENCE</span>
                  </div>
                  {injury.components.map((c) => (
                    <div key={c.name} className="grid grid-cols-12 items-start gap-1 border-t border-white/5 px-3 py-2 text-[11px]">
                      <span className="col-span-3 font-semibold text-white/80">{c.name}</span>
                      <span className="col-span-2 font-mono text-white/50">{c.input}</span>
                      <span className="col-span-1 text-right font-mono" style={{ color: c.points > 0 ? riskColor(Math.min(60, c.points * 2)) : "#4b5563" }}>+{c.points}</span>
                      <span className="col-span-6 leading-snug text-white/50">{c.science}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[10px] leading-snug text-white/45">OCTON estimates this from real inputs (minutes-load, age, freshness, live status) — public medical data isn't available, so it is a transparent, physiology-grounded model, not a clinical diagnosis. Connect a club training feed and these factors sharpen with real GPS/ACWR data.</p>
              </Section>
            )}

            {/* mindset bayesian */}
            {m.bayes && (
              <Section icon={Brain} title="MINDSET · BAYESIAN POSTERIOR" tag={`Beta(α ${contrib?.posterior.alpha ?? by.alpha}, β ${contrib?.posterior.beta ?? by.beta})`}>
                <div className="flex items-baseline justify-between">
                  <p className="font-head text-3xl font-black" style={{ color: mColor(m.morale) }}>{m.morale}%<span className="ml-2 font-body text-sm font-normal text-white/50">{m.state}</span></p>
                  {m.band && <p className="font-mono text-[10px] text-white/40">95% CI {m.band.low}–{m.band.high}% · σ{m.band.std}</p>}
                </div>
                <BetaCurve alpha={contrib?.posterior.alpha ?? by.alpha} beta={contrib?.posterior.beta ?? by.beta} mean={(by.mean ?? m.morale / 100)} band={m.band} />
                <div className="mt-2 grid grid-cols-4 gap-2 font-mono text-[10px] text-white/60">
                  {[["PRECISION", by.precision], ["RECALL", by.recall], ["ACCURACY", by.accuracy], ["CONFIDENCE", by.confidence]].map(([k, v]) => (
                    <div key={k}><span className="block text-white/30">{k}</span>{Math.round((v || 0) * 100)}%</div>
                  ))}
                </div>
                {/* signal contribution table — tap a row to see why it voted */}
                {contrib && (
                  <div className="mt-4 overflow-hidden rounded-lg border border-white/8" data-testid="signal-contributions">
                    <div className="grid grid-cols-6 gap-1 bg-white/[0.04] px-3 py-1.5 font-tac text-[8px] tracking-widest text-white/40">
                      <span className="col-span-3">EVIDENCE SIGNAL · tap for why</span><span>VOTE</span><span>+α</span><span>+β</span>
                    </div>
                    {contrib.signals.map((s) => (
                      <div key={s.name} className="border-t border-white/5">
                        <button onClick={() => setOpenSig(openSig === s.name ? null : s.name)} data-testid={`signal-row-${s.name}`}
                          className="grid w-full grid-cols-6 gap-1 px-3 py-1.5 text-left font-mono text-[10px] text-white/60 transition hover:bg-white/[0.03]">
                          <span className="col-span-3 flex items-center gap-1 capitalize text-white/75"><ChevronRight size={11} className={`transition-transform ${openSig === s.name ? "rotate-90" : ""}`} />{s.name}</span>
                          <span>{Math.round(s.vote * 100)}%</span>
                          <span className="text-[#33d17a]">+{s.add_alpha}</span>
                          <span className="text-[#ff7300]">+{s.add_beta}</span>
                        </button>
                        {openSig === s.name && (
                          <div className="bg-black/40 px-4 py-2.5" data-testid={`signal-detail-${s.name}`}>
                            <p className="text-[11px] leading-snug text-white/60">{s.why}</p>
                            <p className="mt-1.5 font-mono text-[10px] text-white/40">{s.math}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-[10px] leading-snug text-white/45">Each signal casts a weighted vote that adds pseudo-counts to α (match-ready) or β (not). The posterior mean is the morale; its spread is the credible band; precision/recall/accuracy measure how strongly the five signals agree.</p>
              </Section>
            )}

            {/* hebbian resonance */}
            {m.resonance && (
              <Section icon={Cpu} title="512-DIM HEBBIAN RESONANCE" tag={`consolidation ${m.resonance.consolidation}`}>
                <FactorBar label={`Resonance with OCTON's winning profile · best-fit ${m.resonance.aligned_formation}`} value={m.resonance.score} color={mColor(m.resonance.score)} />
                <p className="mt-2 text-[10px] leading-snug text-white/45">How strongly this player's attribute vector fires with the 512-dimensional pattern OCTON has learned wins matches across 118 seasons — projected through a shared 12-axis tactical concept space.</p>
              </Section>
            )}

            {/* AVA common brain + coach read */}
            <Section icon={Radio} title="AVA COMMON BRAIN · FLEET KNOWLEDGE" tag={deep?.source || "recalling…"}>
              {deepLoading ? (
                <div className="flex items-center gap-2 text-[12px] text-white/50" data-testid="deep-read-loading"><Loader2 className="animate-spin" size={15} /> Pulling scientific depth from the mothership &amp; synthesising…</div>
              ) : (
                <div className="space-y-4">
                  {deep?.coach_read && (
                    <div className="rounded-xl border border-cyan-400/20 bg-gradient-to-br from-cyan-400/[0.06] to-transparent p-4" data-testid="coach-read">
                      <p className="mb-1 flex items-center gap-1.5 font-tac text-[9px] tracking-widest txt-cyan"><Sparkles size={11} /> OCTON'S READ</p>
                      <p className="text-[13px] leading-relaxed text-white/80">{deep.coach_read}</p>
                    </div>
                  )}
                  <KnowledgeBlock text={deep?.knowledge} />
                </div>
              )}
            </Section>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
